import type { BasicObject, MakeRequired, Nullable } from "./types"

export type SomeKindOfObject = { [key: string]: unknown }

export function hasRequiredFields<TObject extends SomeKindOfObject, TFields extends keyof TObject>(
  obj: Pick<TObject, TFields>,
  fields: TFields[],
): obj is MakeRequired<TObject, TFields> {
  return fields.every(field => {
    const value = obj[field]

    return value !== null && value !== undefined
  })
}

export function assertRequiredFields<TObject extends SomeKindOfObject, TFields extends keyof TObject>(
  obj: Pick<TObject, TFields>,
  fields: TFields[],
): asserts obj is MakeRequired<TObject, TFields> {
  if (hasRequiredFields(obj, fields)) return

  const expectedFields = fields.map(f => `"${String(f)}"`).join(", ")
  throw new Error(
    `[assertRequiredFields] expected ${JSON.stringify(obj)} object to have non-nullable ${expectedFields} fields`,
  )
}

export function filterByRequiredFields<TObject extends SomeKindOfObject, TFields extends keyof TObject>(
  fields: TFields[],
) {
  return (item: Nullable<Pick<TObject, TFields>>): item is MakeRequired<TObject, TFields> =>
    !!item && hasRequiredFields(item, fields)
}

export const isEmptyObject = (obj: BasicObject): boolean => Object.keys(obj).length === 0

export const isKeyOf = <TObj extends BasicObject>(key: string | number | symbol, obj: TObj): key is keyof TObj =>
  key in obj

export const mapValues = <TObject extends Record<string, unknown>, TResult>(
  obj: TObject,
  fn: (v: TObject[keyof TObject], k: keyof TObject) => TResult,
): { [K in keyof TObject]: TResult } =>
  Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, fn(value as TObject[keyof TObject], key)])) as {
    [K in keyof TObject]: TResult
  }

export const removeEmptyValues = <TValue>(obj: Record<string, Nullable<TValue>>): Record<string, TValue> =>
  Object.fromEntries(
    Object.entries(obj).filter((entry): entry is [string, TValue] => {
      const value = entry[1]
      return value !== null && value !== undefined
    }),
  )
